import StarsIcon from "@mui/icons-material/Stars";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import {
  Alert,
  Box,
  Button,
  Skeleton,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useCallback, useEffect, useState } from "react";
import {
  getMembershipPlans,
  getMyMembership,
  getMyMembershipHistory,
  initiatePayment,
  profilePageVisited,
  getMembership,
  logSubscriptionPageView,
} from "../../api/api";
import MyMembershipPanel from "./MyMembershipPanel";
import SubscriptionCard from "./SubscriptionCard";

const SubscriptionNew = () => {
  const [pricingData, setPricingData] = useState([]);
  const [selectedPlanIndex, setSelectedPlanIndex] = useState(0);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState("");
  const [payingDurationId, setPayingDurationId] = useState(null);
  const [paymentError, setPaymentError] = useState("");
  const [myMembership, setMyMembership] = useState(null);
  const [membershipHistory, setMembershipHistory] = useState([]);
  const [membershipLoading, setMembershipLoading] = useState(true);
  const [membershipError, setMembershipError] = useState("");
  const [isMemberShip, setIsMemberShip] = useState(false);

  const fetchPlans = useCallback(async () => {
    setPlansLoading(true);
    setPlansError("");
    try {
      const response = await getMembershipPlans();
      const plans = response?.data;
      if (!Array.isArray(plans)) {
        throw new Error("Membership plans returned an invalid response.");
      }
      setPricingData(plans.filter((plan) => plan?.durations?.length));
      setSelectedPlanIndex(0);
    } catch (error) {
      setPlansError(
        error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          "Unable to load membership plans.",
      );
    } finally {
      setPlansLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  // Records the visit so the employee team can follow up with customers who
  // looked at pricing but never paid. Tracking must never block the page, so
  // a failure here is swallowed rather than surfaced.
  useEffect(() => {
    logSubscriptionPageView().catch(() => {});
  }, []);

  useEffect(() => {
    const fetchMyMembership = async () => {
      try {
        const [currentResult, historyResult] = await Promise.allSettled([
          getMyMembership(),
          getMyMembershipHistory(),
        ]);

        if (currentResult.status === "rejected") {
          throw currentResult.reason;
        }

        setMyMembership(currentResult.value?.data || { active: false });
        setMembershipHistory(
          historyResult.status === "fulfilled" &&
            Array.isArray(historyResult.value?.data)
            ? historyResult.value.data
            : [],
        );
      } catch (error) {
        setMembershipError(
          error?.response?.data?.error?.message ||
            error?.response?.data?.message ||
            "Unable to load your membership details.",
        );
      } finally {
        setMembershipLoading(false);
      }
    };
    fetchMyMembership();
  }, []);

  const startPhonePePayment = async (plan) => {
    setPaymentError("");
    setPayingDurationId(plan.id);

    try {
      const response = await initiatePayment({
        membership_duration_id: plan.id,
        provider: "phonepe",
      });
      const payment = response?.data;

      if (!payment?.merchant_order_id || !payment?.redirect_url) {
        throw new Error("PhonePe did not return a valid checkout URL.");
      }

      sessionStorage.setItem(
        "pellifix_pending_payment",
        JSON.stringify({
          merchant_order_id: payment.merchant_order_id,
          provider: payment.provider,
          provider_order_id: payment.provider_order_id,
          expire_at: payment.expire_at,
        }),
      );
      window.location.assign(payment.redirect_url);
    } catch (error) {
      setPaymentError(
        error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          "Unable to start PhonePe payment. Please try again.",
      );
      setPayingDurationId(null);
    }
  };

  const selectedPlan = pricingData[selectedPlanIndex];

  useEffect(() => {
    getMembership().then((response) => {
      if (response && response.status >= 200 && response.status <= 300) {
        setIsMemberShip(response?.data?.is_membership);
      }
    });
  }, []);

  useEffect(() => {
    if (!isMemberShip) {
      profilePageVisited().catch(() => {});
    }
  }, [isMemberShip]);

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 1320,
        mx: "auto",
        px: { xs: 1, sm: 2 },
        pb: 5,
      }}
    >
      <MyMembershipPanel
        membership={myMembership}
        history={membershipHistory}
        loading={membershipLoading}
        error={membershipError}
      />

      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          textAlign: "center",
          borderRadius: 4,
          px: 2,
          py: { xs: 4, md: 5 },
          mb: 3,
          background:
            "linear-gradient(135deg, rgba(255,128,0,0.13) 0%, rgba(255,255,255,0.9) 48%, rgba(231,134,92,0.14) 100%)",
          border: "1px solid rgba(255, 128, 0, 0.14)",
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            display: "grid",
            placeItems: "center",
            mx: "auto",
            mb: 1.5,
            borderRadius: "50%",
            bgcolor: "primary.main",
            color: "white",
            boxShadow: "0 10px 25px rgba(255,128,0,.28)",
          }}
        >
          <FavoriteRoundedIcon />
        </Box>
        <Typography variant="h4" fontWeight={800} letterSpacing={-0.8}>
          Find the plan that fits your journey
        </Typography>
        <Typography
          color="text.secondary"
          sx={{ mt: 1, maxWidth: 620, mx: "auto" }}
        >
          Connect with meaningful matches using the access and support level
          that works best for you.
        </Typography>
      </Box>

      {plansError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<RefreshRoundedIcon />}
              onClick={fetchPlans}
            >
              Retry
            </Button>
          }
        >
          {plansError}
        </Alert>
      )}
      {paymentError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {paymentError}
        </Alert>
      )}

      {plansLoading ? (
        <Grid container spacing={2}>
          {[1, 2, 3, 4].map((item) => (
            <Grid key={item} size={{ xs: 12, sm: 6, md: 3 }}>
              <Skeleton
                variant="rounded"
                height={430}
                sx={{ borderRadius: 3 }}
              />
            </Grid>
          ))}
        </Grid>
      ) : pricingData.length ? (
        <>
          <Box
            sx={{ display: "flex", justifyContent: "center", width: "100%" }}
          >
            <Box
              sx={{
                border: 1,
                borderColor: "divider",
                width: { md: "50%", sm: "100%", xs: "100%" },
              }}
            >
              <Tabs
                value={selectedPlanIndex}
                onChange={(event, newValue) => setSelectedPlanIndex(newValue)}
                variant="fullWidth"
                aria-label="Membership plans"
              >
                {pricingData.map((plan) => (
                  <Tab
                    key={plan.id}
                    label={plan.name}
                    icon={plan.name === "Regular" ? null : <StarsIcon />}
                    iconPosition="end"
                    sx={{
                      minHeight: 48,
                      color: "black",
                      "&.Mui-selected": { fontWeight: "bold" },
                    }}
                  />
                ))}
              </Tabs>
            </Box>
          </Box>

          <Typography
            color="text.secondary"
            textAlign="center"
            sx={{ mt: 2.5, mb: 2 }}
          >
            {selectedPlan?.description}
          </Typography>

          <Grid
            container
            spacing={{ xs: 2, md: 2.5 }}
            mt={1}
            alignItems="stretch"
          >
            {selectedPlan?.durations.map((duration) => (
              <Grid key={duration.id} size={{ xs: 12, sm: 6, md: 3 }}>
                <SubscriptionCard
                  planDetails={duration}
                  planName={selectedPlan.name}
                  serviceType={selectedPlan.service_type}
                  currentPlan={
                    myMembership?.active !== false &&
                    myMembership?.membership_name === selectedPlan.name &&
                    myMembership?.duration_label === duration.duration_label
                  }
                  onChoose={startPhonePePayment}
                  paymentDisabled={payingDurationId !== null}
                  paymentLoading={payingDurationId === duration.id}
                />
              </Grid>
            ))}
          </Grid>

          <Box
            sx={{
              mt: 4,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              color: "text.secondary",
            }}
          >
            <SecurityRoundedIcon color="success" fontSize="small" />
            <Typography fontSize={13}>
              Secure payment powered by PhonePe
            </Typography>
          </Box>
        </>
      ) : !plansError ? (
        <Alert severity="info">
          No membership plans are currently available.
        </Alert>
      ) : null}
    </Box>
  );
};

export default SubscriptionNew;
